import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { initPostHog, identifyUser, resetUser, capturePageview } from '@/lib/posthog';

const IS_PRODUCTION = import.meta.env.MODE === 'production';

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { user, profile } = useAuth();
  const previousUserIdRef = useRef<string | null>(null);
  const initializedRef = useRef(false);

  // Initialize PostHog on mount - only in production
  useEffect(() => {
    if (!IS_PRODUCTION) return;
    
    if (!initializedRef.current) {
      initPostHog();
      initializedRef.current = true;
    }
  }, []);

  // Track pageviews on route changes - only in production
  useEffect(() => {
    if (!IS_PRODUCTION) return;
    
    if (initializedRef.current) {
      capturePageview(window.location.href);
    }
  }, [location.pathname, location.search]);

  // Identify/reset user on auth changes - only in production
  useEffect(() => {
    if (!IS_PRODUCTION) return;
    
    if (user && user.id !== previousUserIdRef.current) {
      // User just logged in or changed
      identifyUser(user.id, {
        email: user.email,
        plan: profile?.plan || 'free',
        first_name: profile?.first_name || undefined,
        created_at: profile?.created_at || user.created_at,
      });
      previousUserIdRef.current = user.id;
    } else if (!user && previousUserIdRef.current) {
      // User just logged out
      resetUser();
      previousUserIdRef.current = null;
    }
  }, [user, profile]);

  // Update user traits when profile changes - only in production
  useEffect(() => {
    if (!IS_PRODUCTION) return;
    
    if (user && profile && previousUserIdRef.current === user.id) {
      identifyUser(user.id, {
        email: user.email,
        plan: profile.plan,
        first_name: profile.first_name || undefined,
        credits: profile.credits,
        preferred_language: profile.preferred_language,
      });
    }
  }, [user, profile?.plan, profile?.credits, profile?.first_name]);

  return <>{children}</>;
}
